import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  FETCH_DASHBOARD_ANALYTICS,
  fetchDashboardAnalyticsSuccess,
  fetchDashboardAnalyticsFail,
  fetchDashboardAnalyticsSkip,
} from '../actions/dashboard';
import { fetchAnalyticsData } from '../apis/dashboard';
import {
  buildDashboardCacheKey,
  isDashboardCacheFresh,
  processDashboardAnalytics,
  yieldToBrowser,
} from '../../utils/dashboardAnalytics';

const selectDashboardState = (state) => state.dashboard || {};

const hasDayWiseEntries = (dayWiseData) =>
  Boolean(dayWiseData && Object.keys(dayWiseData).length > 0);

function* fetchDashboardAnalytics(action) {
  const {
    startDate,
    endDate,
    isSingleDay,
    rangeStart,
    rangeEnd,
    forceRefresh = false,
    isMultiDay = false,
  } = action.payload;

  const cacheKey = buildDashboardCacheKey(startDate, endDate);

  try {
    const dashboard = yield select(selectDashboardState);
    const cache = dashboard.cache || {};

    if (
      !forceRefresh &&
      dashboard.activeCacheKey === cacheKey &&
      dashboard.analysisData &&
      (!isMultiDay || hasDayWiseEntries(dashboard.dayWiseData))
    ) {
      yield put(fetchDashboardAnalyticsSkip());
      return;
    }

    if (!forceRefresh) {
      const cachedEntry = cache[cacheKey];
      const cacheHasDayWise = hasDayWiseEntries(cachedEntry?.dayWiseData);
      const cacheComplete =
        Boolean(cachedEntry?.analysisData) &&
        (!cachedEntry?.isMultiDay || cacheHasDayWise);

      if (cachedEntry && isDashboardCacheFresh(cachedEntry) && cacheComplete) {
        yield put(
          fetchDashboardAnalyticsSuccess({
            cacheKey,
            analysisData: cachedEntry.analysisData,
            dayWiseData: cachedEntry.dayWiseData || {},
            fromCache: true,
            fetchedAt: cachedEntry.fetchedAt,
            isMultiDay: Boolean(cachedEntry.isMultiDay),
            alreadyActive:
              dashboard.activeCacheKey === cacheKey &&
              (!cachedEntry.isMultiDay || hasDayWiseEntries(dashboard.dayWiseData)),
          })
        );
        return;
      }
    }

    const rawData = yield call(fetchAnalyticsData, { startDate, endDate });

    yield call(yieldToBrowser);

    const { filteredData, dayWiseData } = yield call(processDashboardAnalytics, {
      rawData,
      startDate,
      endDate,
      isSingleDay,
      rangeStart,
      rangeEnd,
    });

    const fetchedAt = Date.now();

    yield put(
      fetchDashboardAnalyticsSuccess({
        cacheKey,
        analysisData: filteredData,
        dayWiseData: isMultiDay ? dayWiseData : {},
        fromCache: false,
        fetchedAt,
        isMultiDay,
      })
    );
  } catch (error) {
    yield put(fetchDashboardAnalyticsFail(error.message || 'Error fetching dashboard analytics'));
  }
}

export default function* dashboardSaga() {
  yield takeLatest(FETCH_DASHBOARD_ANALYTICS, fetchDashboardAnalytics);
}
